import { useCallback, useState , useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllStudents,
} from "../../application/services/studentManagementService";
import {
  setStudentsList,
} from "../../store/studentManagementReducer";

const useStudentList = () => {
  const dispatch = useDispatch();
  const { students } = useSelector((state) => state.studentManagement);

  const abortControllerRef = useRef(null);
  const studentsRef = useRef(students);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [currentFilters, setCurrentFilters] = useState({});

  studentsRef.current = students;

  const fetchStudentList = useCallback(
    async (pageNum = 1, append = false, filters = {}) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);

      try {
        const filtersToUse = append ? currentFilters : filters;

        if (!append) {
          setCurrentFilters(filters);
          setPage(1);
          setHasMore(true);
        }

        const response = await getAllStudents({
          page: pageNum,
          limit: 10,
          sortBy: "created_at",
          sortOrder: "DESC",
          ...filtersToUse,
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;

        const newData = response?.data || [];

        const updatedList = append
          ? [...(studentsRef.current || []), ...newData]
          : newData;

        dispatch(setStudentsList(updatedList));
        setHasMore(newData.length === 10);
      } catch (error) {
        if (error.code === "ERR_CANCELED") return;

        console.error("Error fetching student list:", error);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    },
    [dispatch, currentFilters]
  );

  const loadMore = useCallback(() => {
    if (!hasMore || loading) return;

    const nextPage = page + 1;
    setPage(nextPage);
    fetchStudentList(nextPage, true, currentFilters);
  }, [hasMore, loading, page, fetchStudentList, currentFilters]);

  return { students, loadMore, hasMore, loading, fetchStudentList };
};

export default useStudentList;